import { ReactNode, useCallback } from 'react';
import { useDropzone, type FileRejection } from 'react-dropzone';

export type DragDropTexts = {
  dragRejectTitle?: string;
  dragActiveTitle?: string;
  dragRejectDescription?: string;
  dragActiveDescription?: string;
  fileTooLarge?: ReactNode;
  invalidFileType?: ReactNode;
  uploadFailed?: ReactNode;
};

export interface UseDragDropWrapperParams {
  maxFileSizeMB?: number;
  accept?: string;
  onFileSelected?: (file: File) => void;
  isReady: boolean;
  dragDropTexts?: DragDropTexts;
  fileError: ReactNode | null;
  setFileError: (error: ReactNode | null) => void;
}

export const useDragDropWrapper = ({
  maxFileSizeMB: maxFileSizeMBProp,
  accept,
  onFileSelected,
  isReady,
  dragDropTexts,
  setFileError,
}: UseDragDropWrapperParams) => {
  const maxFileSizeMB = maxFileSizeMBProp ?? 10;
  const allowedFileTypes = accept?.split(',') ?? ['.csv'];

  const maxFileSize = maxFileSizeMB * 1024 * 1024;

  const fileTooLargeError =
    dragDropTexts?.fileTooLarge ?? `File size exceeds ${maxFileSizeMB}MB limit`;
  const invalidFileTypeError =
    dragDropTexts?.invalidFileType ??
    `Invalid file type. Allowed types: ${allowedFileTypes.join(', ')}`;
  const uploadFailedError = dragDropTexts?.uploadFailed ?? 'Failed to upload file';

  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      setFileError(null);

      // Handle rejected files
      if (rejectedFiles.length > 0) {
        const rejection = rejectedFiles[0];
        if (rejection.errors?.[0]?.code === 'file-too-large') {
          setFileError(fileTooLargeError);
        } else if (rejection.errors?.[0]?.code === 'file-invalid-type') {
          setFileError(invalidFileTypeError);
        } else {
          setFileError(uploadFailedError);
        }
        return;
      }

      if (acceptedFiles.length === 0) {
        return;
      }

      const file = acceptedFiles[0];

      // Additional validation
      const extension = '.' + file.name.split('.').pop()?.toLowerCase();
      if (!allowedFileTypes.includes(extension)) {
        setFileError(invalidFileTypeError);
        return;
      }

      if (file.size > maxFileSize) {
        setFileError(fileTooLargeError);
        return;
      }

      onFileSelected?.(file);
    },
    [
      allowedFileTypes,
      maxFileSize,
      maxFileSizeMB,
      onFileSelected,
      fileTooLargeError,
      invalidFileTypeError,
      uploadFailedError,
    ]
  );

  const acceptMimeTypes = allowedFileTypes.reduce((acc, ext) => {
    if (ext === '.csv') {
      acc['text/csv'] = ['.csv'];
    } else if (ext === '.xlsx') {
      acc['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'] = ['.xlsx'];
    } else if (ext === '.xls') {
      acc['application/vnd.ms-excel'] = ['.xls'];
    }
    return acc;
  }, {} as Record<string, string[]>);

  const { getRootProps, getInputProps, isDragActive, isDragReject } = useDropzone({
    onDrop,
    accept: acceptMimeTypes,
    multiple: false,
    maxFiles: 1,
    maxSize: maxFileSize,
    noClick: true,
    noKeyboard: true,
    disabled: !isReady,
  });

  return {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragReject,
    allowedFileTypes,
  };
};
